import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@workspace/design-system/src/components';

interface SelectItem {
    value: string;
    label: string;
}

interface CustomSelectProps {
    placeholder: string;
    items: SelectItem[];
    value?: string;
    onSelect: (value: string) => void;
    className?: string;
}

export function CustomSelect({ placeholder, items, value, onSelect, className }: CustomSelectProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getSelectedLabel = () => {
        if (!value) return placeholder;
        return items.find(item => item.value === value)?.label || placeholder;
    };

    const handleItemClick = (itemValue: string) => {
        onSelect(itemValue);
        setIsDropdownOpen(false);
    };

    return (
        <div className={`relative w-40 ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full h-10 px-4 bg-[#F5F5F5] rounded-lg flex items-center justify-between text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] hover:bg-[#ECECEC] transition-colors"
            >
                <span>{getSelectedLabel()}</span>
                <Icon name="Down" size={16} color="#101010" />
            </button>

            {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-2xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] py-2 z-10">
                    {items.map((item) => (
                        <button
                            key={item.value}
                            onClick={() => handleItemClick(item.value)}
                            className="w-full px-3 py-2 text-left text-[14px] hover:bg-[#F5F5F5] flex items-center justify-between transition-colors"
                        >
                            <span className={value === item.value ? 'text-[#3C78F4]' : 'text-[#101010]'}>
                                {item.label}
                            </span>
                            <div className="flex items-center justify-center">
                                {value === item.value ? (
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="8" cy="8" r="7" fill="#3C78F4" stroke="#3C78F4" strokeWidth="1" />
                                        <path
                                            d="M5.5 8L7 9.5L10.5 6"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="8" cy="8" r="7" fill="#F0F0F0" strokeWidth="1" />
                                        <path
                                            d="M5.5 8L7 9.5L10.5 6"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
